/**
 * Filtro das mensagens de erro que chegam ao usuário.
 *
 * O toast de erro (`handleError`) exibe o texto que o backend devolveu, e uma
 * falha de infraestrutura carrega detalhe que não pode aparecer na tela: o
 * driver do Postgres monta a mensagem de conexão com host, usuário e nome da
 * base — "failed to connect to `host=10.x.x.x user=postgres database=hera`:
 * server error (FATAL: no more connections allowed (max_client_conn))".
 *
 * Os backends já respondem mensagem amigável nesses casos; esta função é a
 * última linha de defesa, para um endpoint novo (ou legado) que volte a vazar o
 * texto cru não chegar ao usuário. O original continua no console para
 * depuração.
 */

/** Mensagem exibida quando o texto do backend não pode ser mostrado. */
export const MENSAGEM_ERRO_GENERICA =
    "Não foi possível concluir a operação. Tente novamente em alguns instantes; se o problema persistir, contate o suporte.";

/**
 * Assinaturas que só aparecem em erro de infraestrutura (banco, rede, fila) ou
 * em stack trace. São termos técnicos em inglês, então não colidem com as
 * mensagens de negócio, escritas em pt-BR.
 */
const ASSINATURAS_INFRA = [
    "sqlstate",
    "host=",
    "dbname=",
    "database=",
    "user=postgres",
    "max_client_conn",
    "pgbouncer",
    "dial tcp",
    "econnrefused",
    "etimedout",
    "connection refused",
    "connection reset",
    "no such host",
    "i/o timeout",
    "too many connections",
    "failed to connect",
    "server error (fatal",
    "psycopg",
    "sequelize",
    "knex",
    "gorm",
    "traceback (most recent call last)",
    "at java.",
    "goroutine ",
];

/**
 * Devolve um texto seguro para exibir ao usuário.
 *
 * @param {unknown} mensagem Texto vindo do backend.
 * @param {string} [fallback] Mensagem usada quando o texto não serve.
 * @return {string}
 */
export function sanitizeErrorMessage(
    mensagem: unknown,
    fallback: string = MENSAGEM_ERRO_GENERICA,
): string {
    if (typeof mensagem !== "string" || !mensagem.trim()) return fallback;

    const normalizada = mensagem.toLowerCase();

    if (
        ASSINATURAS_INFRA.some((assinatura) => normalizada.includes(assinatura))
    ) {
        console.error(
            "[sanitizeErrorMessage] mensagem de infraestrutura ocultada do usuário:",
            mensagem,
        );
        return fallback;
    }

    return mensagem;
}
